import { c as _c } from "react/compiler-runtime";
import { Avatar, Button, Card, Field, HStack, Input, SimpleGrid, Text, Textarea, VStack, Wrap } from '@chakra-ui/react';
import { UploadButton } from '@src/components/sco/Account/UploadButton';
import { ADVISE_CATEGORY_NAMES, COMP_OPTS, ENGAGE_OPTS, HEADLINE_MAX, MAX_CATEGORY_BLOCKS, NOTE_MAX } from '@src/components/sco/Advisors/data/becomeAdvisor';
import { SkillPicker } from '@src/components/sco/Advisors/sections/advisorApplication/SkillPicker';
import { emptyExperience, MAX_EXPERIENCES, WARM_INTRO_OPTIONS, type AboutYouValues, type AdvisorApplicationValues, type ApplicationFieldErrors } from '@src/components/sco/Advisors/sections/advisorApplication/useAdvisorApplication';
import { LocationInput, LocationInputPlace } from '@src/components/sco/LocationInput';
import { SelectableButtons } from '@src/components/sco/SelectableButtons';
import { experienceTypes } from '@src/constants/experience-types';
import { ScoUser } from '@src/graphql-types';
import { scoBackendUrl } from '@src/lib/startups-url';
import { LuCheck, LuPlus } from 'react-icons/lu';

/** Engagement style labels ↔ API keys (`advisor-engagement-styles`). */
const ENGAGEMENT_STYLE_KEY_BY_LABEL: Record<string, string> = {
  'Scheduled calls': 'scheduled_calls',
  Email: 'email',
  'Chat / DMs': 'chat',
  'In person': 'in_person'
};
const chipProps = {
  buttonProps: {
    paddingY: 2
  },
  iconPlacement: 'left' as const,
  icons: {
    default: LuPlus,
    selected: LuCheck
  }
};

/** Card wrapper so every step reads the same as the `/account` forms. */
const StepCard = t0 => {
  const $ = _c(10);
  const {
    title,
    description,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text fontWeight="semibold">{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description && <Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">{description}</Text>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <VStack align="stretch" gap={1}>{t1}{t2}</VStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <Card.Root variant="subtle"><Card.Body><VStack align="stretch" gap={5}>{t3}{children}</VStack></Card.Body></Card.Root>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
type StepProps = {
  values: AdvisorApplicationValues;
  setValue: <K extends keyof AdvisorApplicationValues>(key: K, value: AdvisorApplicationValues[K]) => void;
  setCategories: (categories: string[]) => void;
  suggestedSkills: string[];
  errors?: ApplicationFieldErrors;
};
type AboutYouStepProps = {
  values: AboutYouValues;
  aboutHydrated: boolean;
  currentUser?: ScoUser | null;
  setAboutValue: <K extends keyof AboutYouValues>(key: K, value: AboutYouValues[K]) => void;
  setLocation: (place: LocationInputPlace | null) => void;
  onUploadError: (message: string) => void;
  errors?: ApplicationFieldErrors;
};

/** Step 1 — User Profile fields founders see on the advisor card. */
export const AboutYouStep = t0 => {
  const $ = _c(92);
  const {
    values,
    aboutHydrated,
    currentUser,
    setAboutValue,
    setLocation,
    onUploadError,
    errors: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const errors = t2;
  let t3;
  if ($[2] !== values.email || $[3] !== values.firstName || $[4] !== values.lastName) {
    t3 = `${values.firstName} ${values.lastName}`.trim() || values.email;
    $[2] = values.email;
    $[3] = values.firstName;
    $[4] = values.lastName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const displayName = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      base: 1,
      md: 2
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = !!errors.firstName;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Field.Label>First name</Field.Label>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = !!errors.firstName;
  let t8;
  if ($[8] !== setAboutValue) {
    t8 = event => setAboutValue("firstName", event.target.value);
    $[8] = setAboutValue;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7 || $[11] !== t8 || $[12] !== values.firstName) {
    t9 = <Input aria-invalid={t7} value={values.firstName} onChange={t8} />;
    $[10] = t7;
    $[11] = t8;
    $[12] = values.firstName;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== errors.firstName) {
    t10 = errors.firstName ? <Field.ErrorText>{errors.firstName}</Field.ErrorText> : null;
    $[14] = errors.firstName;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t5 || $[18] !== t9) {
    t11 = <Field.Root required={true} invalid={t5}>{t6}{t9}{t10}</Field.Root>;
    $[16] = t10;
    $[17] = t5;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = !!errors.lastName;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Field.Label>Last name</Field.Label>;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = !!errors.lastName;
  let t15;
  if ($[21] !== setAboutValue) {
    t15 = event_0 => setAboutValue("lastName", event_0.target.value);
    $[21] = setAboutValue;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t14 || $[24] !== t15 || $[25] !== values.lastName) {
    t16 = <Input aria-invalid={t14} value={values.lastName} onChange={t15} />;
    $[23] = t14;
    $[24] = t15;
    $[25] = values.lastName;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== errors.lastName) {
    t17 = errors.lastName ? <Field.ErrorText>{errors.lastName}</Field.ErrorText> : null;
    $[27] = errors.lastName;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t12 || $[30] !== t16 || $[31] !== t17) {
    t18 = <Field.Root required={true} invalid={t12}>{t13}{t16}{t17}</Field.Root>;
    $[29] = t12;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t11 || $[34] !== t18) {
    t19 = <StepCard description="Founders see this on your advisor card and in the directory." title="Your name"><SimpleGrid columns={t4} gap={4}>{t11}{t18}</SimpleGrid></StepCard>;
    $[33] = t11;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== aboutHydrated || $[37] !== setLocation || $[38] !== values.locationTitle) {
    t20 = aboutHydrated ? <LocationInput key={values.locationTitle || "empty-location"} defaultValue={values.locationTitle ? {
      placeId: "default",
      title: values.locationTitle
    } : null} placeTypes={["(cities)"]} onChange={place => setLocation(place || null)} onClear={() => setLocation(null)} /> : null;
    $[36] = aboutHydrated;
    $[37] = setLocation;
    $[38] = values.locationTitle;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t20) {
    t21 = <StepCard description={"City is enough \u2014 founders like to know where you are. Optional."} title="Location">{t20}</StepCard>;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const t22 = !!errors.email;
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Field.Label>Email</Field.Label>;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  const t24 = !!errors.email;
  let t25;
  if ($[43] !== setAboutValue) {
    t25 = event_1 => setAboutValue("email", event_1.target.value);
    $[43] = setAboutValue;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t24 || $[46] !== t25 || $[47] !== values.email) {
    t26 = <Input aria-invalid={t24} type="email" value={values.email} onChange={t25} />;
    $[45] = t24;
    $[46] = t25;
    $[47] = values.email;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== errors.email) {
    t27 = errors.email ? <Field.ErrorText>{errors.email}</Field.ErrorText> : null;
    $[49] = errors.email;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== t22 || $[52] !== t26 || $[53] !== t27) {
    t28 = <StepCard description="Used for advisor notifications. Not shown publicly on your card." title="Email"><Field.Root required={true} invalid={t22}>{t23}{t26}{t27}</Field.Root></StepCard>;
    $[51] = t22;
    $[52] = t26;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== values.pictureUrl) {
    t29 = values.pictureUrl ? <Avatar.Image src={values.pictureUrl} /> : null;
    $[55] = values.pictureUrl;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== displayName) {
    t30 = <Avatar.Fallback name={displayName} />;
    $[57] = displayName;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== t29 || $[60] !== t30) {
    t31 = <Avatar.Root size="xl">{t29}{t30}</Avatar.Root>;
    $[59] = t29;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== onUploadError) {
    t32 = err => onUploadError(typeof err === "string" ? err : "Photo upload failed.");
    $[62] = onUploadError;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  let t34;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = ["jpeg", "jpg", "png"];
    t34 = {
      size: "sm"
    };
    $[64] = t33;
    $[65] = t34;
  } else {
    t33 = $[64];
    t34 = $[65];
  }
  let t35;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = scoBackendUrl("/assets/upload-image");
    $[66] = t35;
  } else {
    t35 = $[66];
  }
  let t36;
  if ($[67] !== setAboutValue) {
    t36 = url => {
      setAboutValue("pictureUrl", url);
    };
    $[67] = setAboutValue;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  const t37 = currentUser?.userId;
  let t38;
  if ($[69] !== t37) {
    t38 = {
      attachmentType: "User",
      attachmentId: t37,
      contextKey: "profile-picture"
    };
    $[69] = t37;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t32 || $[72] !== t36 || $[73] !== t38) {
    t39 = <UploadButton afterError={t32} allowedExtensions={t33} buttonProps={t34} maxSizeInBytes={10000000} uploadUrl={t35} afterSuccess={t36} storedFile={t38}>Upload photo</UploadButton>;
    $[71] = t32;
    $[72] = t36;
    $[73] = t38;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== setAboutValue || $[76] !== values.pictureUrl) {
    t40 = values.pictureUrl ? <Button size="sm" variant="outline" onClick={() => setAboutValue("pictureUrl", null)}>Remove</Button> : null;
    $[75] = setAboutValue;
    $[76] = values.pictureUrl;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== t39 || $[79] !== t40) {
    t41 = <HStack gap={2} wrap="wrap">{t39}{t40}</HStack>;
    $[78] = t39;
    $[79] = t40;
    $[80] = t41;
  } else {
    t41 = $[80];
  }
  let t42;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Text color="colorPalette.fg/55" fontSize="xs">A square JPEG or PNG under 10MB is best.</Text>;
    $[81] = t42;
  } else {
    t42 = $[81];
  }
  let t43;
  if ($[82] !== t41) {
    t43 = <VStack align="start" gap={2}>{t41}{t42}</VStack>;
    $[82] = t41;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] !== t31 || $[85] !== t43) {
    t44 = <StepCard description="A face photo builds trust faster than a logo. Optional, but strongly recommended." title="Photo"><HStack align="center" gap={5} wrap="wrap">{t31}{t43}</HStack></StepCard>;
    $[84] = t31;
    $[85] = t43;
    $[86] = t44;
  } else {
    t44 = $[86];
  }
  let t45;
  if ($[87] !== t19 || $[88] !== t21 || $[89] !== t28 || $[90] !== t44) {
    t45 = <VStack align="stretch" gap={4}>{t19}{t21}{t28}{t44}</VStack>;
    $[87] = t19;
    $[88] = t21;
    $[89] = t28;
    $[90] = t44;
    $[91] = t45;
  } else {
    t45 = $[91];
  }
  return t45;
};

/** Step 2 — what this advisor covers. */
export const ExpertiseStep = t0 => {
  const $ = _c(30);
  const {
    values,
    setValue,
    setCategories,
    suggestedSkills,
    errors: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const errors = t2;
  const t3 = !!errors.categories;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ADVISE_CATEGORY_NAMES.map(_temp);
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== setCategories) {
    t5 = options => setCategories(options.map(_temp2));
    $[3] = setCategories;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5 || $[6] !== values.categories) {
    t6 = <Wrap gap={2}><SelectableButtons maxSelections={MAX_CATEGORY_BLOCKS} options={t4} selectableButtonProps={chipProps} value={values.categories} onChange={t5} /></Wrap>;
    $[5] = t5;
    $[6] = values.categories;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== errors.categories) {
    t7 = errors.categories ? <Field.ErrorText>{errors.categories}</Field.ErrorText> : null;
    $[8] = errors.categories;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t3 || $[11] !== t6 || $[12] !== t7) {
    t8 = <StepCard description={`Choose up to ${MAX_CATEGORY_BLOCKS}. These drive which open board seats you get matched to.`} title="What can you advise on?"><Field.Root required={true} invalid={t3}>{t6}{t7}</Field.Root></StepCard>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = !!errors.skills;
  const t10 = values.categories.length === 0;
  let t11;
  if ($[14] !== setValue) {
    t11 = skills => setValue("skills", skills);
    $[14] = setValue;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== suggestedSkills || $[17] !== t10 || $[18] !== t11 || $[19] !== values.skills) {
    t12 = <SkillPicker disabled={t10} selected={values.skills} suggested={suggestedSkills} onChange={t11} />;
    $[16] = suggestedSkills;
    $[17] = t10;
    $[18] = t11;
    $[19] = values.skills;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== errors.skills) {
    t13 = errors.skills ? <Field.ErrorText>{errors.skills}</Field.ErrorText> : null;
    $[21] = errors.skills;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13 || $[25] !== t9) {
    t14 = <StepCard title="Your skills" description={"Founders search by skill, so pick the specifics \u2014 not just the broad area. These are the same skills as Account \u2192 Skills & Experiences."}><Field.Root required={true} invalid={t9}>{t12}{t13}</Field.Root></StepCard>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t9;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== t8) {
    t15 = <VStack align="stretch" gap={4}>{t8}{t14}</VStack>;
    $[27] = t14;
    $[28] = t8;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
};

/** Step 3 — how this advisor introduces themselves. */
export const BackgroundStep = t0 => {
  const $ = _c(40);
  const {
    values,
    setValue,
    errors: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const errors = t2;
  const t3 = !!errors.headline;
  const t4 = !!errors.headline;
  let t5;
  if ($[2] !== setValue) {
    t5 = event => setValue("headline", event.target.value.slice(0, HEADLINE_MAX));
    $[2] = setValue;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4 || $[5] !== t5 || $[6] !== values.headline) {
    t6 = <Input aria-invalid={t4} aria-label="Headline" maxLength={HEADLINE_MAX} placeholder="3X founder with 1 exit, now CEO at Acme Labs" value={values.headline} onChange={t5} />;
    $[4] = t4;
    $[5] = t5;
    $[6] = values.headline;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== values.headline.length) {
    t7 = <Field.HelperText>{values.headline.length}/{HEADLINE_MAX}</Field.HelperText>;
    $[8] = values.headline.length;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== errors.headline) {
    t8 = errors.headline ? <Field.ErrorText>{errors.headline}</Field.ErrorText> : null;
    $[10] = errors.headline;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t3 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = <StepCard description="Brief explanation of your background." title="Headline"><Field.Root required={true} invalid={t3}>{t6}{t7}{t8}</Field.Root></StepCard>;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== setValue || $[18] !== values.experiences) {
    t10 = values.experiences.map((experience, index) => <VStack key={experience.clientKey} align="stretch" bg="colorPalette.muted/40" borderRadius="md" gap={3} padding={3}><Field.Root><Field.Label>Type</Field.Label><Wrap gap={2}>{experienceTypes.map(type => <Button key={type.key} size="xs" variant={experience.experienceTypeKey === type.key ? "solid" : "outline"} onClick={() => {
            const next = values.experiences.map((row, rowIndex) => rowIndex === index ? {
              ...row,
              experienceTypeKey: type.key
            } : row);
            setValue("experiences", next);
          }}>{type.title}</Button>)}</Wrap></Field.Root><Field.Root><Field.Label>Organization</Field.Label><Input placeholder={["Google", "Airbnb", "Y Combinator"][index] || "Company or organization"} value={experience.organizationName} onChange={event_0 => {
          const next_0 = values.experiences.map((row_0, rowIndex_0) => rowIndex_0 === index ? {
            ...row_0,
            organizationName: event_0.target.value
          } : row_0);
          setValue("experiences", next_0);
        }} /></Field.Root><Field.Root><Field.Label>Notes</Field.Label><Textarea placeholder={"What you did there \u2014 the short version founders should know."} rows={3} value={experience.positionDescription} onChange={event_1 => {
          const next_1 = values.experiences.map((row_1, rowIndex_1) => rowIndex_1 === index ? {
            ...row_1,
            positionDescription: event_1.target.value
          } : row_1);
          setValue("experiences", next_1);
        }} /></Field.Root><Button alignSelf="flex-start" size="xs" variant="ghost" onClick={() => setValue("experiences", values.experiences.filter((_, rowIndex_2) => rowIndex_2 !== index))}>Remove</Button></VStack>);
    $[17] = setValue;
    $[18] = values.experiences;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== setValue || $[21] !== values.experiences) {
    t11 = values.experiences.length < MAX_EXPERIENCES ? <Button alignSelf="flex-start" size="sm" variant="outline" onClick={() => setValue("experiences", [...values.experiences, emptyExperience(values.experiences.length)])}><LuPlus />Add experience</Button> : null;
    $[20] = setValue;
    $[21] = values.experiences;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <StepCard description="Roles and companies founders would recognise. Optional." title="Notable experiences"><VStack align="stretch" gap={4}>{t10}{t11}</VStack></StepCard>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== setValue) {
    t13 = event_2 => setValue("note", event_2.target.value.slice(0, NOTE_MAX));
    $[26] = setValue;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13 || $[29] !== values.note) {
    t14 = <Textarea maxLength={NOTE_MAX} placeholder="Happy to take a few fintech and SaaS founders through fundraising and early GTM." rows={4} value={values.note} onChange={t13} />;
    $[28] = t13;
    $[29] = values.note;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== values.note.length) {
    t15 = <Field.HelperText>{values.note.length}/{NOTE_MAX}</Field.HelperText>;
    $[31] = values.note.length;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <StepCard description="Shown to founders on your advisor card. Optional." title="A note to founders"><Field.Root>{t14}{t15}</Field.Root></StepCard>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t12 || $[37] !== t16 || $[38] !== t9) {
    t17 = <VStack align="stretch" gap={4}>{t9}{t12}{t16}</VStack>;
    $[36] = t12;
    $[37] = t16;
    $[38] = t9;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};

/** Step 4 — terms, availability, and the intros they can make. */
export const AdvisingStep = t0 => {
  const $ = _c(36);
  const {
    values,
    setValue,
    errors: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const errors = t2;
  const t3 = !!errors.compensationKeys;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = COMP_OPTS.map(_temp3);
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== setValue) {
    t5 = options => setValue("compensationKeys", options.map(_temp4));
    $[3] = setValue;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5 || $[6] !== values.compensationKeys) {
    t6 = <Wrap gap={2}><SelectableButtons options={t4} selectableButtonProps={chipProps} value={values.compensationKeys} onChange={t5} /></Wrap>;
    $[5] = t5;
    $[6] = values.compensationKeys;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== errors.compensationKeys) {
    t7 = errors.compensationKeys ? <Field.ErrorText>{errors.compensationKeys}</Field.ErrorText> : null;
    $[8] = errors.compensationKeys;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t3 || $[11] !== t6 || $[12] !== t7) {
    t8 = <StepCard description={"Pick everything you would consider \u2014 founders see this before they reach out."} title="How would you like to be compensated?"><Field.Root required={true} invalid={t3}>{t6}{t7}</Field.Root></StepCard>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = !!errors.engagementStyleKeys;
  const t10 = values.engagementStyleKeys;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ENGAGE_OPTS.map(_temp5);
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== setValue) {
    t12 = options_0 => setValue("engagementStyleKeys", options_0.map(_temp6));
    $[15] = setValue;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== values.engagementStyleKeys) {
    t13 = <Wrap gap={2}><SelectableButtons selectableButtonProps={chipProps} value={t10} options={t11} onChange={t12} /></Wrap>;
    $[17] = t12;
    $[18] = values.engagementStyleKeys;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== errors.engagementStyleKeys) {
    t14 = errors.engagementStyleKeys ? <Field.ErrorText>{errors.engagementStyleKeys}</Field.ErrorText> : null;
    $[20] = errors.engagementStyleKeys;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t13 || $[23] !== t14 || $[24] !== t9) {
    t15 = <StepCard description="This sets the rough monthly commitment shown on your profile." title="How can founders engage you?"><Field.Root required={true} invalid={t9}>{t13}{t14}</Field.Root></StepCard>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t9;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = WARM_INTRO_OPTIONS.map(_temp7);
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== setValue) {
    t17 = options_1 => setValue("warmIntroKeys", options_1.map(_temp8));
    $[27] = setValue;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t17 || $[30] !== values.warmIntroKeys) {
    t18 = <StepCard description="Warm intros are one of the highest-signal things an advisor offers. Optional." title="Can you make introductions?"><Wrap gap={2}><SelectableButtons options={t16} selectableButtonProps={chipProps} value={values.warmIntroKeys} onChange={t17} /></Wrap></StepCard>;
    $[29] = t17;
    $[30] = values.warmIntroKeys;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t15 || $[33] !== t18 || $[34] !== t8) {
    t19 = <VStack align="stretch" gap={4}>{t8}{t15}{t18}</VStack>;
    $[32] = t15;
    $[33] = t18;
    $[34] = t8;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  return t19;
};
function _temp(name) {
  return {
    key: name,
    title: name
  };
}
function _temp2(option) {
  return option.key;
}
function _temp3(option) {
  return {
    key: option.key,
    title: option.label
  };
}
function _temp4(option_0) {
  return option_0.key;
}
function _temp5(option_1) {
  return {
    key: ENGAGEMENT_STYLE_KEY_BY_LABEL[option_1.label],
    title: option_1.label
  };
}
function _temp6(option_2) {
  return option_2.key;
}
function _temp7(option_3) {
  return {
    key: option_3.key,
    title: option_3.label
  };
}
function _temp8(option_4) {
  return option_4.key;
}